import { DataType } from '@gp/enums/common';

import type { IRecord, IDataTypeable } from '@gp/types/global';
import type { IDPSReportContextVO } from '@gp/types/dpsReport/common';

/**
 * 分离当前已添加的集合中 初始化拉取的集合（GR的数据） 和 手动添加的集合
 * @param list 当前已添加的集合
 * @returns currentInitial 当前已添加的集合中 初始化拉取的集合（GR的数据）
 * @returns currentAdd 当前已添加的集合中 手动添加的集合
 */
const separateCurrentCollection = <T extends IDataTypeable>(
  // 当前已添加的集合
  list: T[],
): {
  // 当前已添加的集合中 初始化拉取的集合（GR的数据）
  currentInitial: T[];
  // 当前已添加的集合中 手动添加的集合
  currentAdd: T[];
} => {
  // 当前已添加的集合中 初始化拉取的集合（GR的数据）
  const currentInitial: T[] = [];
  // 当前已添加的集合中 手动添加的集合
  const currentAdd: T[] = [];

  for (const item of list) {
    const { data_type: dataType } = item || {};
    // 过滤没有dataType的数据，判定为垃圾数据
    if (!dataType) continue;
    // 初始化拉取的数据，放入到初始化拉取的集合中
    if (dataType === DataType.AutoFetched) {
      currentInitial.push(item);
    } else {
      // 手动添加的数据，放入到手动添加的集合中
      currentAdd.push(item);
    }
  }

  return { currentInitial, currentAdd };
};

const getReversedData = (payload?: any, ignoreKeys?: string[]): IRecord<any> => {
  if (!Array.isArray(ignoreKeys) || !ignoreKeys.length || !payload) return {};
  return ignoreKeys.reduce((acc, cur) => {
    const curVal = payload?.[cur];
    if (!curVal) return acc;
    return { ...acc, [cur]: curVal };
  }, {});
};

/**
 * 获取新的初始化拉取的集合
 * 1、commingInitial为空时，直接返回空数组
 * 2、分别生成currentInitialRepeatCheckKeys、newAddRepeatCheckKeys两个key值集合
 * 3、遍历commingInitial集合
 *    1、调用getRepeatCheckKey方法生成当前元素对应的repeatCheckKey
 *    2、如果newAddRepeatCheckKeys集合中不包含当前元素对应的repeatCheckKey，则根据currentInitialRepeatCheckKeys集合以及repeatCheckKey匹配到当前元素 对应的 currentInitial集合中的索引，
 *      再做数据合并处理，并将数据合并处理后的数据添加到newAdd集合中
 * @param currentInitial 当前初始化拉取的集合
 * @param commingInitial 本次数据刷新 初始化拉取的集合
 * @param newAdd 本次数据刷新后 新的手动添加的集合
 * @param ignoreKeys 指定忽略的字段，合并每条数据时，需要忽略的字段
 * @param getRepeatCheckKey 生成重复检验key的方法（判定数据是否相同的标准）
 * @returns 新的初始化拉取的集合
 */
const getNewInitialCollection = <T extends IDPSReportContextVO>(
  currentInitial: T[],
  commingInitial: T[],
  newAdd: T[],
  ignoreKeys: string[],
  getRepeatCheckKey: (payload: any) => string,
): T[] => {
  if (!Array.isArray(commingInitial) || !commingInitial.length) return [];
  const currentInitialRepeatCheckKeys = currentInitial?.map(getRepeatCheckKey) || [];
  const newAddRepeatCheckKeys = newAdd?.map(getRepeatCheckKey) || [];
  const newInitial = [];
  for (const item of commingInitial) {
    const repeatCheckKey = getRepeatCheckKey(item);
    if (!newAddRepeatCheckKeys?.includes(repeatCheckKey)) {
      const index = currentInitialRepeatCheckKeys?.indexOf(repeatCheckKey);
      const indexItem = currentInitial[index];
      // 根据指定忽略的字段 构建 需要保留的对象，在下面对象合并时，避免指定忽略的字段被覆盖
      const reversedItem = getReversedData(indexItem, ignoreKeys);
      newInitial.push({
        ...indexItem,
        ...item,
        ...reversedItem,
      });
    }
  }
  return newInitial;
};

export const refreshCollection = <T1 extends IDPSReportContextVO & IDataTypeable>(params: {
  current: T1[];
  initial: T1[];
  ignoreKeys: string[];
  getRepeatCheckKey: (payload: any) => string;
}): T1[] => {
  const { current, initial, ignoreKeys, getRepeatCheckKey } = params || {};
  // 当前没有添加project，直接返回初始化project数据
  if (!Array.isArray(current) || !current.length) return Array.isArray(initial) && initial.length > 0 ? initial : [];
  // 把当前添加的project拆分为初始化的project数据、手动添加的project数据 两组
  const { currentInitial, currentAdd } = separateCurrentCollection(current);
  // 获取新的初始化拉取的集合
  const newInitial = getNewInitialCollection(currentInitial, initial, currentAdd, ignoreKeys, getRepeatCheckKey);
  return [...newInitial, ...currentAdd];
};
